import { useMemo } from 'react'
import { flatten } from 'lodash-es'
import { Vector3, BufferGeometry, Mesh, Line } from 'three'
import { Tapeline, useTapelineStore } from '@editorStores'
import { useDisposeThreeObjectsOnUnmount } from '@editorHooks'

/**
 * Will read the current tapelines from the store and return targets to be used
 * by DrawController. When tapelines are not visible no targets will be returned
 */
type TapelineFilter = (tapeline: Tapeline) => boolean

const useTapelineSnapTargets = (xyOnlyOrFilter: boolean | TapelineFilter = false) => {
  const xyOnly = typeof xyOnlyOrFilter === 'boolean' ? xyOnlyOrFilter : false
  const lineFilter = typeof xyOnlyOrFilter === 'function' ? xyOnlyOrFilter : undefined

  const isTapelineVisible = useTapelineStore(state => state.isVisible)
  const isTapelineActive = useTapelineStore(state => state.isActive)
  const tapelines = useTapelineStore(state => state.lines)
  const activeFilterViewId = useTapelineStore(state => state.activeFilterViewId)

  const visibleTapelines = useMemo(
    () => tapelines.filter(tapeline => tapeline.filterViewId === activeFilterViewId),
    [tapelines, activeFilterViewId],
  )

  const filteredTapelines = useMemo(
    () =>
      lineFilter ? visibleTapelines.filter(tapeline => lineFilter(tapeline)) : visibleTapelines,
    [lineFilter, visibleTapelines],
  )

  const snapTargets = useMemo(
    () =>
      isTapelineVisible || isTapelineActive
        ? flatten(
            filteredTapelines.map(tapeline => {
              const start = tapeline.start
              const end = tapeline.end
              const startGeometry = new BufferGeometry().setFromPoints([start.v])
              const startPoint = new Mesh(startGeometry)
              const endGeometry = new BufferGeometry().setFromPoints([end.v])
              const endPoint = new Mesh(endGeometry)

              const p1 = new Vector3(start.x, start.y, start.z)
              const p2 = new Vector3(end.x, end.y, end.z)

              if (xyOnly) {
                p1.setZ(0)
                p2.setZ(0)
              }

              const geometry = new BufferGeometry().setFromPoints([p1, p2])
              const line = new Line(geometry)

              return [startPoint, endPoint, line]
            }),
          )
        : [],
    [filteredTapelines, isTapelineVisible, isTapelineActive, xyOnly],
  )

  useDisposeThreeObjectsOnUnmount(snapTargets)

  return snapTargets
}

export default useTapelineSnapTargets
